<template>
  <BaseLayout :title="`${subtask.homework_title}: ${subtask.title}`">
    <!-- Main Content (Center) -->
    <div class="container my-4">
      <div v-if="loading" class="text-center my-4">
        <div class="spinner-border" role="status">
          <span class="sr-only">&nbsp;&nbsp;Loading...</span>
        </div>
      </div>
      <div v-else>
        <div class="card">
          <div class="card-header d-flex justify-content-between align-items-center">
            <h5 class="mb-0">{{ subtask.title }}</h5>
            <div v-if="siblingSubtasks.length > 1" class="d-flex align-items-center gap-2">
              <button class="btn btn-outline-secondary btn-sm" :disabled="!prevSubtask" @click="navigateToSubtask(prevSubtask.id)" title="Previous task">
                <i class="bi bi-arrow-left"></i>
              </button>
              <span class="small text-muted">{{ currentIndex + 1 }} / {{ siblingSubtasks.length }}</span>
              <button class="btn btn-outline-secondary btn-sm" :disabled="!nextSubtask" @click="navigateToSubtask(nextSubtask.id)" title="Next task">
                <i class="bi bi-arrow-right"></i>
              </button>
            </div>
          </div>
          <div class="card-body">
            <div class="card">
              <div class="card-header d-flex justify-content-between align-items-center">
                <h5 class="mb-0">Description</h5>
                <button v-if="isTeacher && !descEditorOpen" class="btn btn-sm btn-outline-secondary"
                        @click="openDescriptionEditor">
                  <i class="bi bi-pencil"></i> Edit Description
                </button>
              </div>
              <div class="card-body">
                <div v-if="!descEditorOpen" v-html="formattedDescription"></div>
                <div v-else>
                  <MarkdownEditor v-model="descContent" :rows="10" />
                  <div class="mt-2 d-flex gap-2">
                    <button class="btn btn-success btn-sm" :disabled="descSaving" @click="saveDescription">
                      <span v-if="descSaving" class="spinner-border spinner-border-sm" role="status"></span>
                      Save
                    </button>
                    <button class="btn btn-secondary btn-sm" @click="cancelDescription">Cancel</button>
                    <button class="btn btn-outline-info btn-sm" @click="openDescriptionDiff">
                      <i class="bi bi-file-diff"></i> View Diff
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
          <!-- Display only when the task is for evaluation -->
          <div v-if="subtask.to_evaluate" class="card-body">
            <div class="row">
              <div class="col-md-6">
                <h5 class="card-title">
                  Submission Files  
                  <i class="bi bi-question-circle" v-tooltip="'Submission files are files that you submit for the task. The submission files must be named exactly same.'"></i>
                </h5>
                <ul class="list-group">
                  <li v-for="file in subtask.submission_file_paths" :key="file" class="list-group-item">
                    {{ getFileName(file) }}
                  </li>
                  <li v-if="subtask.submission_file_paths.length === 0" class="list-group-item alert alert-info">No submission files. Contact teacher, please.</li>
                </ul>
              </div>
              <div v-if="isTeacher || (!isTeacher && subtask.resource_file_paths > 0)" class="col-md-6">
                <h5 class="card-title">
                  Resource Files  
                  <i class="bi bi-question-circle" v-tooltip="'Resource files are necessary for completing the task, but they are not submitted. Not all tasks have resource files.'"></i>
                </h5>
                <ul class="list-group">
                  <li v-for="file in subtask.resource_file_paths" :key="file" class="list-group-item">
                    {{ getFileName(file) }}
                  </li>
                  <li v-if="subtask.resource_file_paths.length === 0" class="list-group-item alert alert-info">No resource files</li>
                </ul>
              </div>
            </div>
          </div>
          <div v-if="subtask.to_evaluate && !isTeacher" class="card-body">
            <div class="list-group-item">
              <div class="card-title mb-0 d-flex align-items-center">
                <h5>
                  Submissions
                  <i class="bi bi-question-circle ms-2" v-tooltip="'Latest submission will be evaluated. Of course, only submitted before deadline.'"></i>
                </h5>
                <span class="ms-3 d-flex align-items-center">
                  <span><small>Sanity check enabled:</small></span>
                  <i v-if="(subtask.test_pre_check_file_paths && subtask.test_pre_check_file_paths.length > 0)" class="bi bi-toggle-on text-success fs-2 ms-1"
                    v-tooltip="'Sanity check is enabled. Results appear in the list below. If Sanity Check was activated later, it won\'t apply to previously sent files.'"
                  ></i>
                  <i v-else class="bi bi-toggle-off text-danger fs-2 ms-1"
                    v-tooltip="'You will only see `Sanity Check: Not available yet`, and this status will not change, as the Sanity Check will not run.'"
                  ></i>
                </span>
              </div>
              <p class="alert alert-warning"><b>Note:</b> Sanity check is only an informational review of submitted files and not an actual evaluation.</p>
              <!-- F13: sanity check state of the latest submission from this page -->
              <PrecheckIndicator
                v-if="followedSubmission"
                :key="followedSubmission.id"
                :submission-id="followedSubmission.id"
                :started-at="followedSubmission.startedAt"
                @finished="onPrecheckFinished"
                @open-log="openSanityCheckModal"
              />
            </div>
            <div v-if="!(subtask.student_submissions && subtask.student_submissions.length)" class="alert alert-info">
              No submissions yet.
            </div>
            <ul v-else class="list-group">
              <li v-for="submission in sortedSubmissions" :key="submission.id" class="list-group-item d-flex justify-content-between align-items-center" data-test="submission-item">
                <span>
                  {{ formatedSubmissionDate(submission.submission_date) }} - Sanity check: 
                  <span v-if="submission.precheck_successfull === true">
                    <i class="bi bi-check-circle-fill text-success" v-tooltip="'Sanity check passed. This is not evaluation of homework\'s task.'"></i>
                  </span>
                  <span v-else-if="submission.precheck_successfull === false">
                    <i class="bi bi-x-circle-fill text-danger" v-tooltip="'Sanity check failed'"></i>
                  </span>
                  <span v-else-if="submission.container_error">
                    <i class="bi bi-exclamation-triangle-fill text-warning" v-tooltip="'Sanity check could not run (system error).'"></i>
                  </span>
                  <span v-else>
                    Not available yet
                  </span>
                </span>
                <span>
                  <button v-if="submission.precheck_log_file_path" class="btn btn-warning btn-sm me-2" @click="openSanityCheckModal(submission.id)">
                    <i class="bi bi-file-earmark-text"></i> View sanity check
                  </button>
                  <button class="btn btn-outline-secondary btn-sm me-2" @click="openDiffModal(submission.id)">
                    <i class="bi bi-file-diff"></i> Diff with previous
                  </button>
                  <!-- Display to impersonate teacher, because submissions are shown only to students -->
                  <button v-if="submission.container_log_path && (isTeacher || isImpersonating)" class="btn btn-info btn-sm me-2" @click="downloadFile(submission.container_log_path)">
                    <i class="bi bi-file-earmark-text"></i> Download container log
                  </button>
                  <button class="btn btn-primary btn-sm" @click="downloadFiles(submission.file_paths, 'submission_files.zip')">
                    <i class="bi bi-download"></i> Download submission
                  </button>
                </span>
              </li>
            </ul>
          </div>
        </div>

        <!-- Teacher-only: pre-check script editor -->
        <div v-if="isTeacher" class="card mt-3">
          <div class="card-header d-flex justify-content-between align-items-center">
            <h5 class="mb-0">Pre-check Script</h5>
            <div v-if="!preCheckEditorOpen" class="d-flex gap-2">
              <button class="btn btn-outline-secondary btn-sm" @click="openScriptHistory('pre_check')">
                <i class="bi bi-clock-history"></i> History
              </button>
              <button class="btn btn-sm btn-outline-secondary" @click="openPreCheckEditor">
                <i class="bi bi-pencil-square"></i> Edit Script
              </button>
            </div>
          </div>
          <div v-if="preCheckEditorOpen" class="card-body">
            <CodeEditor v-model="preCheckContent" />
            <div class="mt-2 d-flex gap-2">
              <button class="btn btn-success btn-sm" :disabled="preCheckSaving" @click="savePreCheckScript">
                <span v-if="preCheckSaving" class="spinner-border spinner-border-sm" role="status"></span>
                Save
              </button>
              <button class="btn btn-secondary btn-sm" @click="cancelPreCheckEditor">Cancel</button>
              <button class="btn btn-outline-info btn-sm" @click="openScriptDiff('pre_check')">
                <i class="bi bi-file-diff"></i> View Diff
              </button>
              <button class="btn btn-outline-secondary btn-sm" @click="openScriptHistory('pre_check')">
                <i class="bi bi-clock-history"></i> History
              </button>
            </div>
          </div>
        </div>

        <!-- Teacher-only: eval script editor -->
        <div v-if="isTeacher" class="card mt-3">
          <div class="card-header d-flex justify-content-between align-items-center">
            <h5 class="mb-0">Evaluation Script</h5>
            <div v-if="!evalEditorOpen" class="d-flex gap-2">
              <button class="btn btn-outline-secondary btn-sm" @click="openScriptHistory('evaluation')">
                <i class="bi bi-clock-history"></i> History
              </button>
              <button class="btn btn-sm btn-outline-secondary" @click="openEvalEditor">
                <i class="bi bi-pencil-square"></i> Edit Script
              </button>
            </div>
          </div>
          <div v-if="evalEditorOpen" class="card-body">
            <CodeEditor v-model="evalContent" />
            <div class="mt-2 d-flex gap-2">
              <button class="btn btn-success btn-sm" :disabled="evalSaving" @click="saveEvalScript">
                <span v-if="evalSaving" class="spinner-border spinner-border-sm" role="status"></span>
                Save
              </button>
              <button class="btn btn-secondary btn-sm" @click="cancelEvalEditor">Cancel</button>
              <button class="btn btn-outline-info btn-sm" @click="openScriptDiff('evaluation')">
                <i class="bi bi-file-diff"></i> View Diff
              </button>
              <button class="btn btn-outline-secondary btn-sm" @click="openScriptHistory('evaluation')">
                <i class="bi bi-clock-history"></i> History
              </button>
            </div>
          </div>
        </div>

        <!-- Teacher-only: AI-authored bug instructions for this subtask -->
        <BugInstructionList v-if="isTeacher && subtask.id" :subtaskId="subtask.id" />

        <!-- Teacher-only: Sanity Check Administration -->
        <div v-if="isTeacher && subtask.id" class="card mt-4">
          <div class="card-header d-flex justify-content-between align-items-center">
            <h5 class="mb-0">Sanity Check Administration</h5>
            <button class="btn btn-warning btn-sm" :disabled="rerunAllDisabled" @click="rerunPreCheckAll">
              <span v-if="rerunAllDisabled" class="spinner-border spinner-border-sm me-1" role="status"></span>
              Re-run All
            </button>
          </div>
          <div class="card-body">
            <!-- Re-run All progress -->
            <div v-if="rerunAllProgress" class="mb-3">
              <div class="d-flex justify-content-between small mb-1">
                <span>Re-running… {{ rerunAllProgress.done }} / {{ rerunAllProgress.total }}</span>
                <span v-if="rerunAllProgress.failed > 0" class="text-danger">{{ rerunAllProgress.failed }} failed</span>
              </div>
              <div class="progress">
                <div class="progress-bar" :style="{ width: (rerunAllProgress.done / rerunAllProgress.total * 100) + '%' }"></div>
              </div>
            </div>
            <!-- Latest submissions per student -->
            <div v-if="latestSubmissions.length === 0" class="alert alert-info">No submissions for this subtask.</div>
            <table v-else class="table table-sm table-bordered">
              <thead>
                <tr>
                  <th>Submission ID</th>
                  <th>Student</th>
                  <th>Submitted</th>
                  <th>Pre-check</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="sub in latestSubmissions" :key="sub.id">
                  <td>{{ sub.id }}</td>
                  <td>{{ sub.student_id }}</td>
                  <td>{{ formatDate(sub.submission_date) }}</td>
                  <td>
                    <span v-if="sub.precheck_successfull === true" class="text-success">Pass</span>
                    <span v-else-if="sub.precheck_successfull === false" class="text-danger">Fail</span>
                    <span v-else class="text-muted">—</span>
                  </td>
                  <td class="d-flex gap-1">
                    <button class="btn btn-warning btn-sm" @click="standardRerun(sub.id)" title="Re-run sanity check and update submission result">Re-run</button>
                    <button class="btn btn-outline-secondary btn-sm" :disabled="testScriptRunning.has(sub.id)" @click="testScript(sub.id)" title="Test script without updating submission result">
                      <span v-if="testScriptRunning.has(sub.id)" class="spinner-border spinner-border-sm me-1" role="status"></span>
                      Test script
                    </button>
                  </td>
                </tr>
              </tbody>
            </table>

            <!-- Recent test run results -->
            <h6 class="mt-3">Recent Test Runs</h6>
            <button class="btn btn-outline-secondary btn-sm mb-2" @click="loadRecentTestRunResults">Refresh</button>
            <div v-if="recentTestRunResults.length === 0" class="alert alert-info">No test run results yet.</div>
            <table v-else class="table table-sm table-bordered">
              <thead>
                <tr>
                  <th>ID</th>
                  <th>Submission</th>
                  <th>Status</th>
                  <th>Time</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="r in recentTestRunResults" :key="r.id">
                  <td>{{ r.id }}</td>
                  <td>{{ r.submission_id }}</td>
                  <td>
                    <span v-if="r.passed === null && !r.error" class="text-muted">Pending</span>
                    <span v-else-if="r.error" class="text-warning fw-bold">ERROR</span>
                    <span v-else-if="r.passed" class="text-success fw-bold">PASSED</span>
                    <span v-else class="text-danger fw-bold">FAILED</span>
                  </td>
                  <td>{{ r.created_at ? formatDate(r.created_at) : '—' }}</td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>

        <!-- Test run result dialog (inline, shown after Re-run) -->
        <div v-if="testRunDialog.visible" class="modal d-block" style="background:rgba(0,0,0,.4);" tabindex="-1">
          <div class="modal-dialog modal-lg">
            <div class="modal-content">
              <div class="modal-header">
                <h5 class="modal-title">Test Script Result (submission #{{ testRunDialog.submissionId }})</h5>
                <button type="button" class="btn-close" @click="closeTestRunDialog"></button>
              </div>
              <div class="modal-body">
                <div v-if="testRunDialog.polling" class="text-center">
                  <div class="spinner-border" role="status"></div>
                  <p class="mt-2">Running sanity check…</p>
                </div>
                <div v-else>
                  <div v-if="testRunDialog.timedOut" class="alert alert-warning">Sanity check timed out — the script exceeded the maximum wait time. Check the server logs or try again.</div>
                  <div v-else-if="testRunDialog.error" class="alert alert-danger">Worker error — sanity check could not run.</div>
                  <div v-else-if="testRunDialog.passed === null" class="alert alert-warning">Result not yet available.</div>
                  <div v-else-if="testRunDialog.passed" class="alert alert-success">PASSED</div>
                  <div v-else class="alert alert-danger">FAILED</div>
                  <pre v-if="testRunDialog.output" class="bg-light p-2 mt-2" style="white-space:pre-wrap;max-height:400px;overflow-y:auto;">{{ testRunDialog.output }}</pre>
                </div>
              </div>
              <div class="modal-footer">
                <button class="btn btn-secondary" @click="closeTestRunDialog">Close</button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Right Sidebar Slot -->
    <template v-slot:right-sidebar>
      <div v-if="subtask.to_evaluate && !isTeacher && !isImpersonating" class="card p-3 mb-3">
        <h5 class="card-title">Submission:</h5>
        <button class="btn btn-success" @click="submitFilesClick" data-bs-toggle="modal" data-bs-target="#submitFilesModal">
          <i class="bi bi-upload"></i> Submit files
        </button>
      </div>
      <div class="card p-3">
        <h5 class="card-title">Actions:</h5>
        <router-link :to="`/homework/${subtask.homework_id}`" class="btn btn-primary mb-2">
            <i class="bi bi-arrow-left"></i> Back to Homework
        </router-link>
        <router-link v-if="isTeacher" :to="`/subtask/edit/${subtask.id}`" class="btn btn-secondary mb-2">
          <i class="bi bi-pencil"></i> Edit Task
        </router-link>
        <button v-if="isTeacher" @click="deleteSubtask(subtask.id)" class="btn btn-danger mb-2">
          <i class="bi bi-trash"></i> Delete Task
        </button>
        <button v-if="isTeacher" class="btn btn-info" @click="downloadFiles(subtask.submission_file_paths, 'subtask_files.zip')">
          <i class="bi bi-download"></i> Download task files
        </button>
      </div>
    </template>

    <!-- Submit Files Modal -->
    <SubmitFilesModal
      v-if="!isTeacher"
      ref="submitFilesModal"
      :required_file_paths="subtask.submission_file_paths"
      @submitForm="uploadSubmissionFiles"
    />

    <StemErrorModal ref="stemErrorModal" />

    <!-- F16a: Sanity check log modal -->
    <div v-if="sanityCheckModal.visible" class="modal d-block" style="background:rgba(0,0,0,.4);" tabindex="-1">
      <div class="modal-dialog modal-lg">
        <div class="modal-content">
          <div class="modal-header">
            <h5 class="modal-title">Sanity Check Log — {{ sanityCheckModal.filename }}</h5>
            <button type="button" class="btn-close" @click="closeSanityCheckModal"></button>
          </div>
          <div class="modal-body">
            <div v-if="sanityCheckModal.loading" class="text-center">
              <div class="spinner-border" role="status"></div>
            </div>
            <div v-else>
              <div v-if="sanityCheckModal.truncated" class="alert alert-warning mb-2">
                Log is large and has been truncated to 256 kB for display.
                <button type="button" class="btn btn-link alert-link p-0 ms-2 align-baseline" @click="downloadFullPrecheckLog">Download full log</button>
              </div>
              <pre class="bg-light p-2" style="white-space:pre-wrap;max-height:60vh;overflow-y:auto;">{{ sanityCheckModal.content }}</pre>
            </div>
          </div>
          <div class="modal-footer">
            <button class="btn btn-secondary" @click="closeSanityCheckModal">Close</button>
          </div>
        </div>
      </div>
    </div>

    <!-- F16b: Script revision history modal -->
    <div v-if="scriptHistoryModal.visible" class="modal d-block" style="background:rgba(0,0,0,.4);" tabindex="-1">
      <div class="modal-dialog modal-lg">
        <div class="modal-content">
          <div class="modal-header">
            <h5 class="modal-title">{{ scriptHistoryModal.scriptType === 'pre_check' ? 'Pre-check' : 'Evaluation' }} Script History</h5>
            <button type="button" class="btn-close" @click="scriptHistoryModal.visible = false"></button>
          </div>
          <div class="modal-body">
            <div v-if="scriptHistoryModal.loading" class="text-center">
              <div class="spinner-border" role="status"></div>
            </div>
            <div v-else-if="scriptHistoryModal.revisions.length === 0" class="alert alert-info">
              No history yet.
            </div>
            <table v-else class="table table-sm table-bordered">
              <thead>
                <tr>
                  <th>Saved at</th>
                  <th>Teacher UCO</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="rev in scriptHistoryModal.revisions" :key="rev.id">
                  <td>{{ formatDate(rev.created_at) }}</td>
                  <td>{{ rev.teacher_uco || rev.teacher_id }}</td>
                  <td>
                    <button class="btn btn-sm btn-outline-info" @click="openScriptDiffById(scriptHistoryModal.scriptType, rev.id)">
                      <i class="bi bi-file-diff"></i> View Diff
                    </button>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
          <div class="modal-footer">
            <button class="btn btn-secondary" @click="scriptHistoryModal.visible = false">Close</button>
          </div>
        </div>
      </div>
    </div>

    <!-- Every diff (submission, script, description) opens in this one popup -->
    <DiffModal ref="diffModal" @error="(message) => $refs.stemErrorModal.show('Error', message)" />
  </BaseLayout>
</template>

<script>
import BaseLayout from '@/components/BaseLayout.vue';
import SubmitFilesModal from '@/components/SubmitFilesModal.vue';
import BugInstructionList from '@/components/BugInstructionList.vue';
import { defineAsyncComponent } from 'vue';
import MarkdownEditor from '@/components/MarkdownEditor.vue';
import SubtaskController from '@/controllers/SubtaskController';
import SubmissionController from '@/controllers/SubmissionController';
import DiffModal from '@/components/DiffModal.vue';
import { savedVersionDiff } from '@/utils/diff';
import StemErrorModal from '@/components/StemErrorModal.vue';
import Submission from '@/models/submission';
import { renderMarkdown } from '@/utils/renderMarkdown';
import { mapGetters } from 'vuex';
import PrecheckIndicator, { MAX_WAIT_MS } from './PrecheckIndicator.vue';

// F13: resume after a reload only for submissions younger than this

export default {
  name: 'SubtaskDetailView',
  components: {
    BaseLayout,
    SubmitFilesModal,
    BugInstructionList,
    CodeEditor: defineAsyncComponent(() => import('@/components/CodeEditor.vue')),
    MarkdownEditor,
    DiffModal,
    StemErrorModal,
    PrecheckIndicator,
  },
  data() {
    return {
      subtask: {},
      siblingSubtasks: [],
      loading: true,
      isDownloading: false,
      // pre-check script editor
      preCheckEditorOpen: false,
      preCheckContent: '',
      preCheckFilename: 'pre_check_submission.sh',
      preCheckSaving: false,
      preCheckOriginalLength: 0,
      // eval script editor
      evalEditorOpen: false,
      evalContent: '',
      evalFilename: 'test_subtask.sh',
      evalSaving: false,
      evalOriginalLength: 0,
      // description editor
      descEditorOpen: false,
      descContent: '',
      descSaving: false,
      latestSubmissions: [],
      recentTestRunResults: [],
      rerunAllDisabled: false,
      rerunAllProgress: null,
      testScriptRunning: new Set(),
      testRunDialog: {
        visible: false,
        submissionId: null,
        polling: false,
        passed: null,
        error: false,
        timedOut: false,
        output: '',
      },
      pollIntervalId: null,
      // F13: { id, startedAt } — key drives PrecheckIndicator mount/unmount
      followedSubmission: null,
      // F16a: sanity check modal
      sanityCheckModal: {
        visible: false,
        content: '',
        filename: '',
        truncated: false,
        submissionId: null,
        loading: false,
      },
      // F16b: script revision history modal
      scriptHistoryModal: {
        visible: false,
        loading: false,
        revisions: [],
        scriptType: '',
      },
    };
  },
  computed: {
    ...mapGetters(['isTeacher', 'userId', 'isImpersonating']),
    currentIndex() {
      return this.siblingSubtasks.findIndex(s => s.id === this.subtask.id);
    },
    prevSubtask() {
      const idx = this.currentIndex;
      return idx > 0 ? this.siblingSubtasks[idx - 1] : null;
    },
    nextSubtask() {
      const idx = this.currentIndex;
      return idx !== -1 && idx < this.siblingSubtasks.length - 1 ? this.siblingSubtasks[idx + 1] : null;
    },
    formattedDescription() {
      return renderMarkdown(this.subtask.description);
    },
    formatedSubmissionDate() {
      return (date) => Submission.formatDate(date);
    },
    sortedSubmissions() {
      return (this.subtask.student_submissions || [])
               .slice()
               .sort((a, b) => new Date(b.submission_date) - new Date(a.submission_date));
    },
  },
  async created() {
    await this.loadSubtask(this.$route.params.id);
  },
  beforeUnmount() {
    this._resetTestRunState();
  },
  watch: {
    '$route.params.id'(newId) {
      this._resetTestRunState();
      this.followedSubmission = null;
      this.loadSubtask(newId);
    },
  },
  methods: {
    // F13: resume after a reload — pass real startedAt so the component uses the right deadline
    resumePrecheckIfRunning() {
      const enabled = this.subtask.test_pre_check_file_paths && this.subtask.test_pre_check_file_paths.length > 0;
      const latest = this.sortedSubmissions[0];
      if (!enabled || !latest || latest.precheck_successfull != null || latest.container_error) return;
      // submission_date is the submitting browser's local time serialized with a 'Z' -> strip 'Z' to parse as local
      const submittedAt = new Date(String(latest.submission_date).replace('Z', ''));
      if (Date.now() - submittedAt > MAX_WAIT_MS) return;
      this.followedSubmission = { id: latest.id, startedAt: submittedAt.getTime() };
    },
    async onPrecheckFinished() {
      this.subtask = await SubtaskController.fetchSubtaskByIdWithStudentsSubmissions(this.subtask.id);
    },
    _resetTestRunState() {
      if (this.pollIntervalId) {
        clearInterval(this.pollIntervalId);
        this.pollIntervalId = null;
      }
      this.testRunDialog = {
        visible: false,
        submissionId: null,
        polling: false,
        passed: null,
        error: false,
        timedOut: false,
        output: '',
      };
      this.testScriptRunning = new Set();
    },
    async loadSubtask(subtaskId) {
      this.loading = true;
      try {
        if (this.isTeacher) {
          this.subtask = await SubtaskController.fetchSubtaskById(subtaskId);
        } else {
          this.subtask = await SubtaskController.fetchSubtaskByIdWithStudentsSubmissions(subtaskId);
          this.resumePrecheckIfRunning();
        }
        const siblings = await SubtaskController.fetchSubtasksByHomeworkId(this.subtask.homework_id);
        this.siblingSubtasks = siblings.slice().sort((a, b) => a.number - b.number);
        if (this.isTeacher) {
          await Promise.all([
            this.loadLatestSubmissions(subtaskId),
            this.loadRecentTestRunResults(),
          ]);
        }
      } catch (error) {
        this.$refs.stemErrorModal.show('Error', error.message);
      } finally {
        this.loading = false;
      }
    },
    async loadLatestSubmissions(subtaskId) {
      try {
        this.latestSubmissions = await SubtaskController.getLatestSubmissionsPerStudent(subtaskId);
      } catch (error) {
        this.$refs.stemErrorModal.show('Error', error.message);
      }
    },
    async loadRecentTestRunResults() {
      try {
        this.recentTestRunResults = await SubtaskController.getRecentTestRunResults(this.subtask.id);
      } catch (error) {
        this.$refs.stemErrorModal.show('Error', error.message);
      }
    },
    async standardRerun(submissionId) {
      try {
        await SubtaskController.rerunPreCheck(this.subtask.id, submissionId, false);
        this.$store.dispatch('triggerAlert', { type: 'success', title: 'Success', message: 'Re-run triggered. Submission result will update when the check finishes.' });
        await this.loadLatestSubmissions(this.subtask.id);
      } catch (error) {
        this.$refs.stemErrorModal.show('Error', error.message);
      }
    },
    async testScript(submissionId) {
      if (this.testScriptRunning.has(submissionId)) return;
      this.testScriptRunning = new Set([...this.testScriptRunning, submissionId]);
      try {
        const result = await SubtaskController.rerunPreCheck(this.subtask.id, submissionId, true);
        this.testRunDialog = {
          visible: true,
          submissionId,
          polling: true,
          passed: null,
          error: false,
          timedOut: false,
          output: '',
        };
        this._startPolling(result.test_result_id, submissionId);
      } catch (error) {
        this.testScriptRunning = new Set([...this.testScriptRunning].filter(id => id !== submissionId));
        this.$refs.stemErrorModal.show('Error', error.message);
      }
    },
    _startPolling(resultId, submissionId) {
      if (this.pollIntervalId) {
        clearInterval(this.pollIntervalId);
      }
      const deadline = Date.now() + 480000;
      let myInterval;
      const finish = (updates) => {
        clearInterval(myInterval);
        if (this.pollIntervalId === myInterval) this.pollIntervalId = null;
        if (this.testRunDialog.submissionId !== submissionId) return;
        this.testRunDialog.polling = false;
        Object.assign(this.testRunDialog, updates);
        this.testScriptRunning = new Set([...this.testScriptRunning].filter(id => id !== submissionId));
        this.loadRecentTestRunResults();
      };
      myInterval = setInterval(async () => {
        if (Date.now() > deadline) {
          finish({ timedOut: true });
          return;
        }
        try {
          const r = await SubtaskController.pollTestRunResult(resultId);
          if (r.passed !== null || r.error) {
            finish({ passed: r.passed, error: r.error, output: r.output || '' });
          }
        } catch (e) {
          if (e.response && e.response.status === 404) {
            finish({ error: true });
          } else {
            finish({ error: true });
            this.$refs.stemErrorModal.show('Error', e.message);
          }
        }
      }, 2000);
      this.pollIntervalId = myInterval;
    },
    closeTestRunDialog() {
      if (this.pollIntervalId) {
        clearInterval(this.pollIntervalId);
        this.pollIntervalId = null;
      }
      if (this.testRunDialog.submissionId !== null) {
        this.testScriptRunning = new Set([...this.testScriptRunning].filter(id => id !== this.testRunDialog.submissionId));
      }
      this.testRunDialog.visible = false;
    },
    async rerunPreCheckAll() {
      if (this.latestSubmissions.length === 0) return;
      const total = this.latestSubmissions.length;
      if (!confirm(`Re-run sanity check for all ${total} submission(s)?`)) return;
      this.rerunAllDisabled = true;
      this.rerunAllProgress = { done: 0, total, failed: 0 };
      const errors = [];
      for (const sub of this.latestSubmissions) {
        try {
          await SubtaskController.rerunPreCheck(this.subtask.id, sub.id, false);
        } catch {
          errors.push(sub.id);
          this.rerunAllProgress.failed++;
        }
        this.rerunAllProgress.done++;
      }
      this.rerunAllProgress = null;
      this.rerunAllDisabled = false;
      const succeeded = total - errors.length;
      if (errors.length === 0) {
        this.$store.dispatch('triggerAlert', { type: 'success', title: 'Success', message: `Re-run triggered for all ${total} submission(s).` });
      } else {
        this.$store.dispatch('triggerAlert', { type: 'warning', title: 'Partial success', message: `${succeeded} of ${total} re-runs triggered. Failed submissions: ${errors.join(', ')}.` });
      }
      await this.loadLatestSubmissions(this.subtask.id);
    },
    formatDate(dateStr) {
      if (!dateStr) return '—';
      return new Date(dateStr).toLocaleString();
    },
    navigateToSubtask(subtaskId) {
      this.$router.push(`/subtask/${subtaskId}`);
    },
    getFileName(filePath) {
      return filePath.split('/').pop();
    },
    async deleteSubtask(subtaskId) {
      if (confirm('Are you sure you want to delete this task?')) {
        try {
          await SubtaskController.deleteSubtask(subtaskId);
          this.$store.dispatch('triggerAlert', {
            type: 'success',
            title: 'Success',
            message: 'Task deleted successfully',
          });
          this.$router.push(`/homework/${this.subtask.homework_id}`);
        } catch (error) {
          this.$refs.stemErrorModal.show('Error', error.message);
        }
      }
    },
    async uploadSubmissionFiles(filesToSubmit) {
      try {
        if (filesToSubmit.length > 0) {
          const formData = new FormData();
          for (const file of filesToSubmit) {
            formData.append('files', file);
          }

          const response = await SubmissionController.createSubmission(this.subtask.id, formData);
          if (response?.data?.submission_id) {
            this.followedSubmission = { id: response.data.submission_id, startedAt: Date.now() };   // F13: start before list refresh so a failed refresh doesn't suppress the indicator
          }
          const subtaskId = this.$route.params.id;
          this.subtask = await SubtaskController.fetchSubtaskByIdWithStudentsSubmissions(subtaskId);

          this.$store.dispatch('triggerAlert', {
            type: 'success',
            title: 'Success',
            message: 'Files submitted successfully',
          });
        }
      } catch (error) {
        this.$refs.stemErrorModal.show('Error', error.message);
      }
    },
    // Download single file
    async downloadFile(filePath) {
      try {
        const response = await SubtaskController.downloadFile(filePath);
        const url = window.URL.createObjectURL(new Blob([response.data]));
        const link = document.createElement('a');
        link.href = url;
        link.setAttribute('download', this.getFileName(filePath));
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
      } catch (error) {
        this.$refs.stemErrorModal.show('Error', error.message);
      }
    },
    // Download multiple files
    async downloadFiles(filePaths, zipName) {
      try {
        const response = await SubtaskController.downloadFiles(filePaths);
        const url = window.URL.createObjectURL(new Blob([response.data]));
        const link = document.createElement('a');
        link.href = url;
        link.setAttribute('download', zipName || 'files.zip');
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
      } catch (error) {
        this.$refs.stemErrorModal.show('Error', error.message);
      }
    },
    // ── pre-check editor ─────────────────────────────────────────────────────
    async openPreCheckEditor() {
      const paths = this.subtask.test_pre_check_file_paths;
      // D24: button always visible; >1 files → warning, editor does not open
      if (paths?.length > 1) {
        this.$refs.stemErrorModal.show('Warning',
          'Multiple files detected — editor supports single-file subtasks. Use file upload.');
        return;
      }
      try {
        if (paths?.length === 1) {
          const { content, filename } = await SubtaskController.getPreCheckScriptContent(this.subtask.id);
          this.preCheckContent = content;
          this.preCheckFilename = filename;
          this.preCheckOriginalLength = content.length;
          this.preCheckLoadedContent = content;
        } else {
          this.preCheckContent = '#!/bin/bash\n';
          this.preCheckFilename = 'pre_check_submission.sh';
          this.preCheckOriginalLength = 0;
          this.preCheckLoadedContent = '#!/bin/bash\n';
        }
        this.preCheckEditorOpen = true;
      } catch (e) {
        this.$refs.stemErrorModal.show('Error', e.message);
      }
    },
    async savePreCheckScript() {
      if (!this.preCheckContent?.trim()) {
        this.$refs.stemErrorModal.show('Error', 'Cannot save empty content.');
        return;
      }
      const ratio = this.preCheckOriginalLength > 0
        ? this.preCheckContent.length / this.preCheckOriginalLength : 1;
      if (ratio < 0.2 && !confirm('Content is much shorter than the original. Are you sure you want to save?')) return;
      this.preCheckSaving = true;
      try {
        await SubtaskController.saveScriptContent(this.subtask.id, 'pre_check', this.preCheckContent);
        this.preCheckEditorOpen = false;
        await this.loadSubtask(this.subtask.id);
      } catch (e) {
        this.$refs.stemErrorModal.show('Error', e.message);
      } finally {
        this.preCheckSaving = false;
      }
    },
    cancelPreCheckEditor() {
      if (this.preCheckContent !== this.preCheckLoadedContent
          && !confirm('Discard unsaved changes?')) return;
      this.preCheckEditorOpen = false;
    },

    // ── eval script editor ────────────────────────────────────────────────────
    async openEvalEditor() {
      const paths = this.subtask.test_file_paths;
      if (paths?.length > 1) {
        this.$refs.stemErrorModal.show('Warning',
          'Multiple files detected — editor supports single-file subtasks. Use file upload.');
        return;
      }
      try {
        if (paths?.length === 1) {
          const { content, filename } = await SubtaskController.getEvaluationScriptContent(this.subtask.id);
          this.evalContent = content;
          this.evalFilename = filename;
          this.evalOriginalLength = content.length;
          this.evalLoadedContent = content;
        } else {
          this.evalContent = '#!/bin/bash\n';
          this.evalFilename = 'test_subtask.sh';
          this.evalOriginalLength = 0;
          this.evalLoadedContent = '#!/bin/bash\n';
        }
        this.evalEditorOpen = true;
      } catch (e) {
        this.$refs.stemErrorModal.show('Error', e.message);
      }
    },
    async saveEvalScript() {
      if (!this.evalContent?.trim()) {
        this.$refs.stemErrorModal.show('Error', 'Cannot save empty content.');
        return;
      }
      const ratio = this.evalOriginalLength > 0
        ? this.evalContent.length / this.evalOriginalLength : 1;
      if (ratio < 0.2 && !confirm('Content is much shorter than the original. Are you sure you want to save?')) return;
      this.evalSaving = true;
      try {
        await SubtaskController.saveScriptContent(this.subtask.id, 'evaluation', this.evalContent);
        this.evalEditorOpen = false;
        await this.loadSubtask(this.subtask.id);
      } catch (e) {
        this.$refs.stemErrorModal.show('Error', e.message);
      } finally {
        this.evalSaving = false;
      }
    },
    cancelEvalEditor() {
      if (this.evalContent !== this.evalLoadedContent
          && !confirm('Discard unsaved changes?')) return;
      this.evalEditorOpen = false;
    },

    // ── description editor ────────────────────────────────────────────────────
    openDescriptionEditor() {
      this.descContent = this.subtask.description || '';
      this.descEditorOpen = true;
    },
    async saveDescription() {
      this.descSaving = true;
      try {
        await SubtaskController.updateSubtask(this.subtask.id, {
          description: this.descContent,
          number: this.subtask.number,
          title: this.subtask.title,
          to_evaluate: this.subtask.to_evaluate,
          homework_id: this.subtask.homework_id,
        });
        this.descEditorOpen = false;
        await this.loadSubtask(this.subtask.id);
      } catch (e) {
        this.$refs.stemErrorModal.show('Error', e.message);
      } finally {
        this.descSaving = false;
      }
    },
    cancelDescription() {
      if (this.descContent !== (this.subtask.description || '')
          && !confirm('Discard unsaved changes?')) return;
      this.descEditorOpen = false;
    },

    submitFilesClick() {
      this.$store.dispatch('refreshToken');
    },

    async downloadFullPrecheckLog() {
      try {
        const response = await SubmissionController.downloadPrecheckLog(this.sanityCheckModal.submissionId);
        const url = window.URL.createObjectURL(new Blob([response.data]));
        const link = document.createElement('a');
        link.href = url;
        link.setAttribute('download', this.sanityCheckModal.filename || 'precheck.log');
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
      } catch (error) {
        this.$refs.stemErrorModal.show('Error', error.message);
      }
    },

    // ── F16a: sanity check modal ───────────────────────────────────────────────
    async openSanityCheckModal(submissionId) {
      this.sanityCheckModal.visible = true;
      this.sanityCheckModal.loading = true;
      this.sanityCheckModal.submissionId = submissionId;
      try {
        const data = await SubmissionController.getPrecheckLogContent(submissionId);
        this.sanityCheckModal.content = data.content;
        this.sanityCheckModal.filename = data.filename;
        this.sanityCheckModal.truncated = data.truncated;
      } catch (e) {
        this.sanityCheckModal.visible = false;
        this.$refs.stemErrorModal.show('Error', e.message);
      } finally {
        this.sanityCheckModal.loading = false;
      }
    },
    closeSanityCheckModal() {
      this.sanityCheckModal.visible = false;
    },

    // ── F16a: submission diff ─────────────────────────────────────────────────
    openDiffModal(submissionId) {
      this.$refs.diffModal.open('Diff with Previous Submission', async () => {
        const data = await SubmissionController.getDiffWithPrevious(submissionId);
        if (!data.has_previous) return { emptyText: 'No previous submission to compare against.' };
        const notices = [];
        if (data.multiple_files) notices.push(`Only ${data.current_filename} is compared.`);
        if (data.previous_content === null) notices.push(`The previous submission has no ${data.current_filename}.`);
        return {
          previous: data.previous_content,
          current: data.current_content || '',
          notices,
          oldLabel: data.previous_submission_date
            ? `Previous (${new Date(data.previous_submission_date).toLocaleString()})`
            : 'Previous version',
        };
      });
    },

    // ── F16b: script history modal ────────────────────────────────────────────
    async openScriptHistory(scriptType) {
      this.scriptHistoryModal.visible = true;
      this.scriptHistoryModal.loading = true;
      this.scriptHistoryModal.scriptType = scriptType;
      try {
        this.scriptHistoryModal.revisions = await SubtaskController.getScriptRevisions(this.subtask.id, scriptType);
      } catch (e) {
        this.scriptHistoryModal.visible = false;
        this.$refs.stemErrorModal.show('Error', e.message);
      } finally {
        this.scriptHistoryModal.loading = false;
      }
    },
    openScriptDiffById(scriptType, revisionId) {
      this.scriptHistoryModal.visible = false;
      this.openScriptDiff(scriptType, revisionId);
    },

    // ── F16b: script / description diff ───────────────────────────────────────
    openScriptDiff(scriptType, revisionId = null) {
      const title = scriptType === 'pre_check' ? 'Pre-check Script — Diff' : 'Evaluation Script — Diff';
      this.$refs.diffModal.open(title, async () =>
        savedVersionDiff(await SubtaskController.getScriptDiff(this.subtask.id, scriptType, revisionId)));
    },
    openDescriptionDiff() {
      this.$refs.diffModal.open('Description — Diff with Previous', async () =>
        savedVersionDiff(await SubtaskController.getDescriptionDiff(this.subtask.id)));
    },
  },
};
</script>